import AttachFileIcon from '@mui/icons-material/AttachFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import {
  Box,
  Button,
  CircularProgress,
  // eslint-disable-next-line react/forbid-elements
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import PDFViewer from 'app/components/PDFViewer';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import defaultColors from 'app/theme/defaultColors';
import { useMobileResourcePicker } from 'app/hooks/useMobileResourcePicker';
import { dataUrlToFile } from 'app/util/dataUrlUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { FileWithUri } from './__types__/MobileOptionMenu.types';
import MobileOptionMenu from './MobileOptionMenu';

// SEE-1091 — Figma receipt-box values (node 5538-151930) with no equivalent in the
// app theme palette: the dashed dropzone border ("Primary Container" #d8e2ff — the
// theme's primaryLight is #B2CCFF) and the helper-text grey (Figma "Gray 1" #333333;
// nearest theme token text.primary is #212121). Everything else maps to theme tokens
// (primary.main border, text.secondary instruction, primary.dark link, lightBlueColor
// surface). Promote these to defaultColors.ts if the pattern recurs.
const RECEIPT_DROPZONE_BORDER = '#D8E2FF';
const RECEIPT_HELPER_TEXT = '#333333';

interface MobileReceiptPreviewProps {
  viewOnly: boolean;
  scopeId?: string;
  receiptUrl?: string;
  receiptUrls?: string[];
  onFileSelect?: (file: File) => void;
  onError?: (error: Error, context: string) => void;
  onReceiptDelete?: (index: number) => void;
  isDeletingReceipt?: boolean;
  // Opt-in: render the compact "Select a file or drag here to upload" dropzone
  // (reimbursement design) for the empty state instead of the tall upload box.
  compactDropzone?: boolean;
}

const MobileReceiptPreview = ({
  onFileSelect,
  viewOnly,
  scopeId,
  receiptUrl,
  receiptUrls,
  onError,
  onReceiptDelete,
  isDeletingReceipt,
  compactDropzone,
}: MobileReceiptPreviewProps) => {
  const theme = useTheme();
  const location = useLocation();
  // SEE-1091 — the redesigned (Figma) receipt upload box is gated behind the
  // equipment flag; flag off keeps the original dropzone for every other mobile
  // user (this component is shared by transactions + reimbursements).
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);
  const [isPermissionMenuOpen, setIsPermissionMenuOpen] = useState(false);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevUrlsLengthRef = useRef<number>(0);
  const processedImgPreviewRef = useRef<string | undefined>(undefined);
  const processedSelectedFilesRef = useRef<FileWithUri[]>([]);
  const pendingClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const receiptCountWhenProcessedRef = useRef<number>(0);
  const displayUrlsLengthRef = useRef<number>(0);
  const {
    imgPreview,
    selectedFiles,
    openCamera,
    openStoragePicker,
    openPicturePicker,
    clearImgPreview,
    clearSelectedFiles,
    isAndroid,
    inferMimeFromName,
    inferMimeFromBase64,
  } = useMobileResourcePicker(scopeId);
  const { t } = useTranslation();

  const isTransactionsRoute = location.pathname.includes('/transactions');

  // Determine which URLs to display: prefer receiptUrls array, fallback to single receiptUrl
  // Filter out empty or invalid URLs
  const displayUrls = useMemo(() => {
    const urls: string[] = [];

    if (receiptUrls && receiptUrls.length > 0) {
      urls.push(...receiptUrls.filter((url) => url && url.trim().length > 0));
    } else if (receiptUrl && receiptUrl.trim().length > 0) {
      urls.push(receiptUrl);
    }

    return urls;
  }, [receiptUrls, receiptUrl]);

  // Update displayUrlsLengthRef whenever displayUrls changes
  useEffect(() => {
    displayUrlsLengthRef.current = displayUrls.length;
  }, [displayUrls.length]);

  // Reset index when URLs change
  useEffect(() => {
    const currentLength = displayUrls.length;
    const previousLength = prevUrlsLengthRef.current;
    if (currentLength !== previousLength) {
      setCurrentReceiptIndex(0);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
      }

      // If displayUrls increased, it means a new receipt was added
      // Clear the preview since the new receipt is now in displayUrls
      if (currentLength > previousLength) {
        // Clear any pending timeout
        if (pendingClearTimeoutRef.current) {
          clearTimeout(pendingClearTimeoutRef.current);
          pendingClearTimeoutRef.current = null;
        }
        // Clear the preview if we have one and the receipt count increased
        // This means the new receipt we were waiting for has appeared
        if (imgPreview || selectedFiles.length > 0) {
          clearImgPreview();
          clearSelectedFiles();
          // Reset the receipt count tracker
          receiptCountWhenProcessedRef.current = 0;
        }
      }

      prevUrlsLengthRef.current = currentLength;
    }
  }, [displayUrls, imgPreview, selectedFiles.length, clearImgPreview, clearSelectedFiles]);

  // Update current receipt index based on scroll position
  useEffect(() => {
    if (displayUrls.length <= 1 || !scrollContainerRef.current) {
      setCurrentReceiptIndex(0);
      return;
    }

    const container = scrollContainerRef.current;
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const itemWidth = container.clientWidth;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      const maxIndex = displayUrls.length - 1;
      setCurrentReceiptIndex(Math.min(currentIndex, maxIndex));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [displayUrls.length]);

  const base64ToBlob = useCallback((b64: string, mime = 'application/pdf') => {
    const byteChars = atob(b64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }, []);

  const handleDownloadPdf = useCallback(
    (url: string, fileName = 'receipt.pdf') => {
      try {
        if (url.startsWith('data:application/pdf;base64,')) {
          const base64 = url.split(',')[1] ?? '';
          const blob = base64ToBlob(base64, 'application/pdf');
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        } else if (url.startsWith('blob:')) {
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (url.startsWith('http://') || url.startsWith('https://')) {
          window.open(url, '_blank');
        }
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Failed to download PDF'), 'PDF download');
      }
    },
    [base64ToBlob, onError]
  );

  const fileUrls = useMemo(() => {
    const urls: string[] = [];
    selectedFiles.forEach((file) => {
      try {
        const isRealFile = file instanceof File;
        const asAny = file as FileWithUri & { base64?: string };
        const name = asAny?.name || '';
        const isPdfByName = name.toLowerCase().endsWith('.pdf');
        const type =
          file.type ||
          inferMimeFromName(name) ||
          (asAny.base64 ? inferMimeFromBase64(asAny.base64) : 'image/jpeg');

        const finalType = isPdfByName ? 'application/pdf' : type;

        if (isRealFile) {
          if (!file.size || file.size === 0) throw new Error('File has no content (size is 0)');
          const url = URL.createObjectURL(file);
          urls.push(url);
          return;
        }

        if (asAny?.base64) {
          if (finalType === 'application/pdf' || isPdfByName) {
            const dataUrl = `data:application/pdf;base64,${asAny.base64}`;
            urls.push(dataUrl);
          } else {
            const dataUrl = `data:${finalType};base64,${asAny.base64}`;
            urls.push(dataUrl);
          }
          return;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MobileReceiptPreview] Error processing file:', error);
      }
    });

    return urls;
  }, [inferMimeFromBase64, inferMimeFromName, selectedFiles]);

  useEffect(() => {
    return () => {
      fileUrls.forEach((url) => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
  }, [fileUrls]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingClearTimeoutRef.current) {
        clearTimeout(pendingClearTimeoutRef.current);
        pendingClearTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!onFileSelect) {
      return;
    }

    const base64ToFile = (b64: string, name: string, mime = 'application/pdf') => {
      const blob = base64ToBlob(b64, mime);
      return new File([blob], name, { type: mime, lastModified: Date.now() });
    };

    const processSelectedFiles = async () => {
      // Only process if selectedFiles is different from the last processed one
      // Compare by length and first file name to detect changes
      const firstFile = selectedFiles.length > 0 ? selectedFiles[0] : null;
      const currentFilesKey = firstFile
        ? `${selectedFiles.length}-${
            firstFile instanceof File ? firstFile.name : (firstFile as FileWithUri).name
          }`
        : '';

      const lastProcessedFirstFile =
        processedSelectedFilesRef.current.length > 0 ? processedSelectedFilesRef.current[0] : null;
      const lastProcessedKey = lastProcessedFirstFile
        ? `${processedSelectedFilesRef.current.length}-${
            lastProcessedFirstFile instanceof File
              ? lastProcessedFirstFile.name
              : (lastProcessedFirstFile as FileWithUri).name
          }`
        : '';

      if (selectedFiles.length > 0 && currentFilesKey !== lastProcessedKey) {
        // If there's a pending preview from a previous file, clear it first
        if (imgPreview) {
          clearImgPreview();
        }
        try {
          let fileToSelect: File;
          if (selectedFiles[0] instanceof File) {
            fileToSelect = selectedFiles[0];
          } else {
            const fwu = selectedFiles[0] as FileWithUri & { base64?: string };
            const name = fwu.name || 'receipt.jpg';
            const type =
              fwu.type ||
              inferMimeFromName(name) ||
              (fwu.base64 ? inferMimeFromBase64(fwu.base64) : 'image/jpeg');

            if (fwu.base64) {
              fileToSelect = base64ToFile(fwu.base64, name, type);
            } else {
              captureException(
                new Error('File does not have base64 and the URI is file:// (not readable in WebView).')
              );
              throw new Error('File does not have base64 and the URI is file:// (not readable in WebView).');
            }
          }

          onFileSelect?.(fileToSelect);

          // Mark these files as processed
          processedSelectedFilesRef.current = [...selectedFiles];

          // Store the current receipt count when processing starts
          receiptCountWhenProcessedRef.current = displayUrlsLengthRef.current;

          // Don't clear the preview automatically - it will be cleared when:
          // 1. The new receipt appears in displayUrls (handled in the displayUrls useEffect)
          // 2. A new file is selected (the new preview will replace the old one)
          // This ensures the preview stays visible until the new receipt is actually added
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          const context = 'MobileReceiptPreview: selectedFiles processing';

          // eslint-disable-next-line no-console
          console.error(`Error in ${context}:`, error);
          onError?.(error instanceof Error ? error : new Error(errorMessage), context);
          captureException(error);
        }
      }
    };

    const processImgPreview = () => {
      // Only process if imgPreview is different from the last processed one
      if (imgPreview && imgPreview !== processedImgPreviewRef.current) {
        // If there are pending selectedFiles from a previous selection, clear them first
        if (selectedFiles.length > 0) {
          clearSelectedFiles();
        }
        try {
          const mimeType = imgPreview.match(/data:(.*?);/)?.[1] || 'image/jpeg';
          const extension = mimeType.split('/')[1] || 'jpg';

          const file = dataUrlToFile(imgPreview, `receipt.${extension}`);

          onFileSelect?.(file);

          // Mark this imgPreview as processed
          processedImgPreviewRef.current = imgPreview;

          // Store the current receipt count when processing starts
          receiptCountWhenProcessedRef.current = displayUrlsLengthRef.current;

          // Don't clear the preview automatically - it will be cleared when:
          // 1. The new receipt appears in displayUrls (handled in the displayUrls useEffect)
          // 2. A new file is selected (the new preview will replace the old one)
          // This ensures the preview stays visible until the new receipt is actually added
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          const context = 'MobileReceiptPreview: imgPreview processing';

          // eslint-disable-next-line no-console
          console.error(`Error in ${context}:`, error);
          onError?.(error instanceof Error ? error : new Error(errorMessage), context);
          captureException(error);
        }
      }
    };

    if (selectedFiles.length > 0) {
      processSelectedFiles();
    } else if (imgPreview) {
      processImgPreview();
    }
  }, [
    selectedFiles,
    imgPreview,
    onFileSelect,
    onError,
    base64ToBlob,
    inferMimeFromBase64,
    inferMimeFromName,
    clearImgPreview,
    clearSelectedFiles,
  ]);

  const handlePermissionSelect = useCallback(
    (action: string) => {
      setIsPermissionMenuOpen(false);
      switch (action) {
        case 'camera':
          openCamera();
          break;
        case 'library':
          openPicturePicker();
          break;
        case 'file':
          openStoragePicker();
          break;
        case 'cancel':
          break;
      }
    },
    [openCamera, openStoragePicker, openPicturePicker]
  );

  const permissionsOptions = [
    {
      name: 'Take Photo',
      action: 'camera',
      icon: <CameraAltIcon color="action" />,
      onSelect: () => handlePermissionSelect('camera'),
    },
    {
      name: 'Choose From Library',
      action: 'library',
      icon: <PhotoLibraryIcon color="action" />,
      onSelect: () => handlePermissionSelect('library'),
    },
    {
      name: 'Choose File',
      action: 'file',
      icon: <AttachFileIcon color="action" />,
      onSelect: () => handlePermissionSelect('file'),
    },
    {
      name: 'Cancel',
      action: 'cancel',
      icon: <CloseIcon color="action" />,
      onSelect: () => setIsPermissionMenuOpen(false),
    },
  ];

  // Render a single receipt item (image or PDF)
  const renderReceiptItem = useCallback(
    (url: string, index: number) => {
      const isPdf = url.toLowerCase().includes('.pdf') || url.startsWith('data:application/pdf');
      const fileName = url.split('/').pop()?.split('?')[0] || `receipt-${index + 1}.pdf`;

      if (isPdf && isAndroid()) {
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              gap: 2,
              flexShrink: 0,
            }}
          >
            <DownloadIcon sx={{ fontSize: 54, color: theme.palette.common.white }} />
            <Typography variant="body1" color="common.white">
              {fileName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPdf(url, fileName);
              }}
              sx={{ mt: 2 }}
            >
              {t('mobileTransactionDetails.downloadReceipt') || 'Download Receipt'}
            </Button>
          </Box>
        );
      }

      return isPdf ? (
        <Box key={index} sx={{ width: '100%', height: '100%', flexShrink: 0 }}>
          <PDFViewer
            url={url}
            width="100%"
            height="100%"
            onError={(error) => onError?.(error, `PDFViewer rendering (receipt ${index + 1})`)}
          />
        </Box>
      ) : (
        <Box
          key={index}
          component="img"
          src={url}
          alt={`Receipt ${index + 1}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            flexShrink: 0,
          }}
          onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            // eslint-disable-next-line no-console
            console.error(`[MobileReceiptPreview] Receipt ${index + 1} image load error:`, url);
            onError?.(
              new Error(`Failed to load receipt image ${index + 1}`),
              `Image rendering (receipt ${index + 1})`
            );
          }}
        />
      );
    },
    [isAndroid, theme, handleDownloadPdf, t, onError]
  );

  // Render preview content for new file selection
  const renderNewFilePreview = useCallback(() => {
    if (selectedFiles.length > 0 && fileUrls.length > 0) {
      const file = selectedFiles[0];
      const url = fileUrls[0];
      const isRealFile = file instanceof File;
      const asAny = file as FileWithUri & { base64?: string };
      const name = isRealFile ? (file as File)?.name ?? '' : asAny?.name ?? '';
      const fileType =
        file.type ||
        (asAny?.base64 ? inferMimeFromBase64(asAny.base64) : undefined) ||
        inferMimeFromName(name);
      const isPdfByName = name.toLowerCase().endsWith('.pdf');
      const isPdf =
        fileType === 'application/pdf' ||
        url.startsWith('data:application/pdf') ||
        (url.startsWith('blob:') && isPdfByName) ||
        isPdfByName;

      if (isPdf && isAndroid()) {
        const fileName = name || 'receipt.pdf';
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              gap: 2,
            }}
          >
            <DownloadIcon sx={{ fontSize: 54, color: theme.palette.common.white }} />
            <Typography variant="body1" color="common.white">
              {fileName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon sx={{ color: theme.palette.common.white }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPdf(url, fileName);
              }}
              sx={{ mt: 2 }}
            >
              {t('mobileTransactionDetails.downloadReceipt') || 'Download Receipt'}
            </Button>
          </Box>
        );
      }

      return isPdf ? (
        <PDFViewer
          url={url}
          width="100%"
          height="100%"
          onError={(error) => {
            // eslint-disable-next-line no-console
            console.error('[MobileReceiptPreview] PDFViewer error:', error);
            onError?.(error, 'PDFViewer rendering');
          }}
        />
      ) : (
        <Box
          component="img"
          src={url}
          alt="Receipt"
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            // eslint-disable-next-line no-console
            console.error('[MobileReceiptPreview] Image load error:', url);
            onError?.(new Error('Failed to load receipt image'), 'Image rendering (selectedFiles)');
          }}
        />
      );
    }

    if (imgPreview) {
      return (
        <Box
          component="img"
          src={imgPreview}
          alt="Receipt"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            // eslint-disable-next-line no-console
            console.error('[MobileReceiptPreview] Image preview load error');
            onError?.(new Error('Failed to load receipt preview'), 'Image preview rendering');
          }}
        />
      );
    }

    return null;
  }, [
    selectedFiles,
    fileUrls,
    imgPreview,
    inferMimeFromBase64,
    inferMimeFromName,
    isAndroid,
    theme,
    handleDownloadPdf,
    t,
    onError,
  ]);

  // Render the upload box (reusable component)
  // SEE-1091 — the equipment-flag receipt box (Figma 5538-151930): highlight-blue
  // surface + white primary-bordered card + dashed dropzone. Shared by both empty
  // upload-box call sites (below-carousel and the no-receipts empty state) so the
  // redesign can't drift between them. Click opens the same file picker.
  const renderEquipmentUploadBox = useCallback(
    () => (
      <Box
        onClick={() => setIsPermissionMenuOpen(true)}
        sx={{
          cursor: 'pointer',
          width: '100%',
          backgroundColor: defaultColors.lightBlueColor,
          borderRadius: '10px',
          p: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: 16, lineHeight: '24px', fontWeight: 300 }}>
          {t('mobileTransactionDetails.pleaseUploadOrSelectReceipt')}
        </Typography>
        <Box
          sx={{
            backgroundColor: 'common.white',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: '16px',
            p: '16px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              border: `1px dashed ${RECEIPT_DROPZONE_BORDER}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              p: '6px',
              minHeight: 50,
            }}
          >
            <AttachFileIcon sx={{ fontSize: 22, color: RECEIPT_HELPER_TEXT }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 12, lineHeight: '16px', letterSpacing: '0.5px', fontWeight: 500 }}>
                <Box component="span" sx={{ color: 'primary.dark' }}>
                  {t('mobileTransactionDetails.selectAFile', 'Select a file')}
                </Box>
                <Box component="span" sx={{ color: RECEIPT_HELPER_TEXT }}>
                  {t('mobileTransactionDetails.dragHereToUpload', ' or drag here to upload')}
                </Box>
              </Typography>
              <Typography
                sx={{ fontSize: 10, lineHeight: '16px', letterSpacing: '0.4px', color: RECEIPT_HELPER_TEXT }}
              >
                {t('mobileTransactionDetails.supportedFileTypes', 'Supported file types: JPG, PNG, PDF')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    ),
    [t]
  );

  const renderUploadBox = useCallback(
    (showContent: boolean) => {
      if (viewOnly) return null;

      // Show preview if there's a new file selected
      const hasNewFile = selectedFiles.length > 0 || imgPreview;

      // SEE-1091 — equipment-flag redesign of the empty upload box.
      if (isEquipmentCodingEnabled && showContent && !hasNewFile) {
        return renderEquipmentUploadBox();
      }

      return (
        <Box
          sx={{
            px: 2,
            py: 3,
            width: '100%',
            height: 300,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            backgroundColor: 'transparent',
            mt: displayUrls.length > 0 ? 2 : 0,
          }}
          onClick={() => setIsPermissionMenuOpen(true)}
        >
          {hasNewFile ? (
            renderNewFilePreview()
          ) : showContent ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('mobileTransactionDetails.pleaseUploadOrSelectReceipt')}
              </Typography>
              <Button variant="outlined" color="primary" sx={{ width: '100%', borderRadius: 2 }}>
                {t('mobileTransactionDetails.uploadReceiptOrFile')}
              </Button>
            </>
          ) : null}
        </Box>
      );
    },
    [
      viewOnly,
      theme,
      displayUrls.length,
      t,
      renderNewFilePreview,
      imgPreview,
      selectedFiles.length,
      isEquipmentCodingEnabled,
      renderEquipmentUploadBox,
    ]
  );

  const handleConfirmDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    if (pendingDeleteIndex !== null) {
      onReceiptDelete?.(pendingDeleteIndex);
      setPendingDeleteIndex(null);
    }
  }, [onReceiptDelete, pendingDeleteIndex]);

  return (
    <Box>
      {/* If we have receipts, show horizontal scroll carousel */}
      {displayUrls.length > 0 ? (
        <Box>
          <Box
            sx={{
              width: '100%',
              height: 300,
              position: 'relative',
            }}
          >
            <Box
              ref={scrollContainerRef}
              sx={{
                display: 'flex',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                width: '100%',
                height: '100%',
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.primary.main,
                  borderRadius: 2,
                },
              }}
            >
              {displayUrls.map((url, index) => (
                <Box
                  key={`receipt-${index}-${url.substring(0, 20)}`}
                  sx={{
                    minWidth: '100%',
                    width: '100%',
                    height: '100%',
                    scrollSnapAlign: 'start',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {renderReceiptItem(url, index)}
                  {!viewOnly && onReceiptDelete && (
                    <IconButton
                      data-testid={`delete-receipt-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteIndex(index);
                        setDeleteConfirmOpen(true);
                      }}
                      disabled={isDeletingReceipt}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                        zIndex: 1,
                      }}
                      size="small"
                    >
                      {isDeletingReceipt ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
            {/* Receipt counter indicator */}
            {displayUrls.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                }}
              >
                {`${currentReceiptIndex + 1} / ${displayUrls.length}`}
              </Box>
            )}
          </Box>
          {/* Upload box below carousel for adding another receipt */}
          {renderUploadBox(true)}
          {/* MobileOptionMenu for adding receipts */}
          {!viewOnly && (
            <MobileOptionMenu
              open={isPermissionMenuOpen}
              onClose={() => setIsPermissionMenuOpen(false)}
              title={'Receipt'}
              options={permissionsOptions}
              onSelect={(option) => option.onSelect?.()}
              sx={{ height: 'min-content' }}
            />
          )}
        </Box>
      ) : isEquipmentCodingEnabled && !viewOnly && !(selectedFiles.length > 0 || imgPreview) ? (
        // SEE-1091 — equipment-flag redesign for the no-receipts empty state
        // (this is the box the Transaction-details step-1 screenshot shows). The
        // picker menu must render here too — it lives inside the original dashed
        // box we're replacing, so without it the click would open nothing.
        <>
          {renderEquipmentUploadBox()}
          <MobileOptionMenu
            open={isPermissionMenuOpen}
            onClose={() => setIsPermissionMenuOpen(false)}
            title={'Receipt'}
            options={permissionsOptions}
            onSelect={(option) => option.onSelect?.()}
            sx={{ height: 'min-content' }}
          />
        </>
      ) : (
        <Box
          sx={{
            px: 2,
            py: compactDropzone && !(selectedFiles.length > 0 || imgPreview) ? 1.5 : 3,
            width: '100%',
            height: compactDropzone && !(selectedFiles.length > 0 || imgPreview) ? 'auto' : 300,
            border: viewOnly
              ? 'none'
              : compactDropzone
              ? `1px dashed ${theme.palette.surfaceContainerHigh.main}`
              : `2px dashed ${theme.palette.divider}`,
            borderRadius: compactDropzone ? 3 : 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: viewOnly ? 'default' : 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            backgroundColor: 'transparent',
          }}
          onClick={() => !viewOnly && setIsPermissionMenuOpen(true)}
        >
          {selectedFiles.length > 0 || imgPreview ? (
            <>
              {selectedFiles.length > 0 && fileUrls.length > 0 ? (
                (() => {
                  const file = selectedFiles[0];
                  const url = fileUrls[0];
                  const isRealFile = file instanceof File;
                  const asAny = file as FileWithUri & { base64?: string };
                  const name = isRealFile ? (file as File)?.name ?? '' : asAny?.name ?? '';
                  const fileType =
                    file.type ||
                    (asAny?.base64 ? inferMimeFromBase64(asAny.base64) : undefined) ||
                    inferMimeFromName(name);
                  const isPdfByName = name.toLowerCase().endsWith('.pdf');
                  const isPdf =
                    fileType === 'application/pdf' ||
                    url.startsWith('data:application/pdf') ||
                    (url.startsWith('blob:') && isPdfByName) ||
                    isPdfByName;

                  if (isPdf && isAndroid()) {
                    const fileName = name || 'receipt.pdf';
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          gap: 2,
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 54, color: theme.palette.common.white }} />
                        <Typography variant="body1" color="common.white">
                          {fileName}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon sx={{ color: theme.palette.common.white }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPdf(url, fileName);
                          }}
                          sx={{ mt: 2 }}
                        >
                          {t('mobileTransactionDetails.downloadReceipt') || 'Download Receipt'}
                        </Button>
                      </Box>
                    );
                  }

                  return isPdf ? (
                    <PDFViewer
                      url={url}
                      width="100%"
                      height="100%"
                      onError={(error) => {
                        // eslint-disable-next-line no-console
                        console.error('[MobileReceiptPreview] PDFViewer error:', error);
                        onError?.(error, 'PDFViewer rendering');
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={url}
                      alt="Receipt"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                        // eslint-disable-next-line no-console
                        console.error('[MobileReceiptPreview] Image load error:', url);
                        onError?.(
                          new Error('Failed to load receipt image'),
                          'Image rendering (selectedFiles)'
                        );
                      }}
                    />
                  );
                })()
              ) : imgPreview ? (
                <Box
                  component="img"
                  src={imgPreview}
                  alt="Receipt"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    // eslint-disable-next-line no-console
                    console.error('[MobileReceiptPreview] Image preview load error');
                    onError?.(new Error('Failed to load receipt preview'), 'Image preview rendering');
                  }}
                />
              ) : null}
              {!isTransactionsRoute && (selectedFiles.length > 0 || imgPreview) && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1,
                    background: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    textAlign: 'center',
                  }}
                >
                  {t('transactions.clickToChangeReceipt')}
                </Box>
              )}
            </>
          ) : !viewOnly ? (
            compactDropzone ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <AttachFileIcon sx={{ fontSize: 22, color: 'neutralVariant25.main' }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: '16px' }}>
                    <Box component="span" sx={{ color: 'primary.dark' }}>
                      {t('reimbursementPage.submitForm.dropzoneSelectFile')}
                    </Box>
                    <Box component="span" sx={{ color: 'neutralVariant25.main' }}>
                      {t('reimbursementPage.submitForm.dropzoneOrDragText')}
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize: 11, lineHeight: '16px', color: 'neutralVariant25.main' }}>
                    {t('reimbursementPage.submitForm.supportedFileTypes')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('mobileTransactionDetails.pleaseUploadOrSelectReceipt')}
                </Typography>
                <Button variant="outlined" color="primary" sx={{ width: '100%', borderRadius: 2 }}>
                  {t('mobileTransactionDetails.uploadReceiptOrFile')}
                </Button>
              </>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('mobileTransactionDetails.noReceiptAvailable')}
            </Typography>
          )}
          {!viewOnly && (
            <MobileOptionMenu
              open={isPermissionMenuOpen}
              onClose={() => setIsPermissionMenuOpen(false)}
              title={'Receipt'}
              options={permissionsOptions}
              onSelect={(option) => option.onSelect?.()}
              sx={{ height: 'min-content' }}
            />
          )}
        </Box>
      )}
      {/* eslint-disable-next-line react/forbid-elements -- compact alert that must not go full-screen */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteIndex(null);
        }}
        data-testid="delete-receipt-dialog"
        maxWidth="xs"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            mx: 3,
            py: 1,
            maxWidth: 340,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 0.5 }}>
          {t('mobileTransactionDetails.deleteReceipt')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t('mobileTransactionDetails.confirmDeleteReceipt')}</DialogContentText>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setPendingDeleteIndex(null);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="primary">
            {t('common.delete', 'Delete')}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default MobileReceiptPreview;
